import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Input, Select, Typography } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PermissionGate } from "@/shared/components/PermissionGate";
import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import { DepartmentFormModal } from "../components/DepartmentFormModal";
import { DepartmentsExplorerTable } from "../components/DepartmentsExplorerTable";
import { MoveDepartmentModal } from "../components/MoveDepartmentModal";
import {
	useCreateDepartment,
	useDepartmentsList,
	useMoveDepartment,
	useUpdateDepartment,
} from "../hooks/useDepartments";
import type { Department, DepartmentFormValues } from "../types";

const { Title } = Typography;

export function DepartmentsPage() {
	const { t } = useTranslation("departments");
	const [formOpen, setFormOpen] = useState(false);
	const [editing, setEditing] = useState<Department | null>(null);
	const [moveTarget, setMoveTarget] = useState<Department | null>(null);
	const [search, setSearch] = useState("");
	const [isActive, setIsActive] = useState(true);
	// Server tomonidagi qidiruv — har harf emas, yozish to'xtagach so'raladi.
	const debouncedSearch = useDebouncedValue(search.trim());

	// ⚠️ Forma (`DepartmentFormModal`) va Ko'chirish (`MoveDepartmentModal`)
	// modallari HAMON to'liq, sahifalanmagan ro'yxatga tayanadi (`DepartmentTreeSelect`
	// ichida) — bu ATAYLAB (3-reja boshidagi izohga qarang), `GET /departments`
	// shakli o'zgarmagan.
	const { data: departments = [] } = useDepartmentsList();

	const createDepartment = useCreateDepartment();
	const updateDepartment = useUpdateDepartment();
	const moveDepartment = useMoveDepartment();

	const closeForm = () => {
		setFormOpen(false);
		setEditing(null);
	};

	const handleSubmit = (values: DepartmentFormValues) => {
		if (editing) {
			// Ota-onani shu forma orqali o'zgartirib bo'lmaydi — buning uchun
			// alohida "Ko'chirish" modali bor.
			updateDepartment.mutate(
				{
					id: editing.id,
					input: { name: values.name, gradeId: values.gradeId, code: values.code },
				},
				{ onSuccess: closeForm }
			);
		} else {
			createDepartment.mutate(values, { onSuccess: closeForm });
		}
	};

	return (
		<div>
			<div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
				<div>
					<Title level={3} className="!mb-0">
						{t("page.title")}
					</Title>
					<span className="text-gray-400 text-sm">{t("page.subtitle")}</span>
				</div>
				<PermissionGate resource="departments" action="create">
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={() => {
							setEditing(null);
							setFormOpen(true);
						}}
					>
						{t("page.createButton")}
					</Button>
				</PermissionGate>
			</div>

			<div className="mb-4 flex gap-3 flex-wrap">
				<Input
					placeholder={t("page.searchPlaceholder")}
					prefix={<SearchOutlined />}
					allowClear
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					style={{ width: 280 }}
				/>
				{/*
				 * `allowClear` YO'Q — backend'da «hammasi» qiymati yo'q (`isActive`
				 * standarti `true`), tozalash «filtr yo'q» degan yolg'on taassurot
				 * berardi (`ZonesPage` bilan bir xil).
				 */}
				<Select
					value={isActive}
					onChange={setIsActive}
					options={[
						{ value: true, label: t("page.statusActive") },
						{ value: false, label: t("page.statusArchived") },
					]}
					style={{ width: 160 }}
				/>
			</div>

			{/*
			 * ⚠️ `key` MAJBURIY: `usePaginatedQuery` sahifani O'Z ichida
			 * (`useState`) saqlaydi, jadval esa hookni o'zi chaqiradi — sahifa
			 * uni `setPage(1)` bilan qaytara olmaydi. Usiz 3-sahifada turib
			 * qidirish/filtrlash 3-sahifani so'rab BO'SH jadval berardi. Kalit
			 * DEBOUNCE qilingan qiymatdan — aks holda har harf qayta mount qilardi.
			 */}
			<DepartmentsExplorerTable
				key={`${debouncedSearch}|${isActive}`}
				q={debouncedSearch || undefined}
				isActive={isActive}
				onEdit={(dept) => {
					setEditing(dept);
					setFormOpen(true);
				}}
				onMove={(dept) => setMoveTarget(dept)}
			/>

			<DepartmentFormModal
				open={formOpen}
				editing={editing}
				departments={departments}
				isSubmitting={createDepartment.isPending || updateDepartment.isPending}
				onCancel={closeForm}
				onSubmit={handleSubmit}
			/>

			<MoveDepartmentModal
				open={moveTarget !== null}
				department={moveTarget}
				departments={departments}
				isSubmitting={moveDepartment.isPending}
				onCancel={() => setMoveTarget(null)}
				onSubmit={(parentId) => {
					if (moveTarget) {
						moveDepartment.mutate(
							{ id: moveTarget.id, parentId },
							{ onSuccess: () => setMoveTarget(null) }
						);
					}
				}}
			/>
		</div>
	);
}
